import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, Card, CardContent, Typography, Button, 
  IconButton, Tooltip 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { 
  getWhatsappTemplatesStore, 
  storeCreateWhatsappTemplate, 
  storeUpdateWhatsappTemplate,
  storeDeleteWhatsappTemplate 
} from '../../../../../store/ratings';
import { useResponseHandler } from '../../../../../hooks/useResponseHandler';
import { ModalConfirmDeactivation } from '../../../../../components/modals/ModalConfirmDeactivation';
import { WhatsappTemplateModal } from './WhatsappTemplateModal';
import { hasPemissions } from '../../../../../commons/utils';

export const WhatsappTemplatesCard = () => {
  const dispatch = useDispatch<any>();
  const { whatsapp_templates_list } = useSelector((state: any) => state.ratingsDevices);
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  
  const [openModalDeactivation, setOpenModalDeactivation] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.plantillas.links', 'view');
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.plantillas.links', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.plantillas.links', 'delete');
    if (del) setCanDelete(true);

    const create = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.plantillas.links', 'create');
    if (create) setCanCreate(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      const fetchData = async () => {
        const response = await dispatch(getWhatsappTemplatesStore());
        await handleResponse(response, 'obtener plantillas para compartir link', false);
      };
      fetchData();
    }
  }, [canView]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSave = async (data: { title: string; content: string }) => {
    let response;
    if (editingTemplate) {
      response = await dispatch(storeUpdateWhatsappTemplate(editingTemplate.id, data));
      await handleResponse(response, 'actualizar plantilla', true);
    } else {
      response = await dispatch(storeCreateWhatsappTemplate(data));
      await handleResponse(response, 'crear plantilla', true);
    }
    
    if (response?.ok) {
      setIsModalOpen(false);
    }
  };

  const handleDelete = (id: string) => {
    setTemplateToDelete(id);
    setOpenModalDeactivation(true);
  };

  const confirmDeactivate = async () => {
    if (!templateToDelete) return;
    const response = await dispatch(storeDeleteWhatsappTemplate(templateToDelete));
    await handleResponse(response, 'eliminar plantilla', true);
    setOpenModalDeactivation(false);
    setTemplateToDelete(null);
  };

  if (!hasPermission) return <>{ResponseComponents}</>;

  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)' }}>
      {ResponseComponents}
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Plantillas de Compartir (WhatsApp)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea mensajes predefinidos para compartir los enlaces con tus clientes.
            </Typography>
          </Box>
          {canCreate && (
            <Button
              variant="contained"
              onClick={handleOpenCreate}
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none', borderRadius: 2, height: 40, px: 3, bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
            >
              Nueva Plantilla
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          {(!whatsapp_templates_list || whatsapp_templates_list.length === 0) ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4, border: '1px dashed #ccc', borderRadius: 2 }}>
              No hay plantillas configuradas aún.
            </Typography>
          ) : (
            whatsapp_templates_list.map((template: any) => (
              <Box 
                key={template.id} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2,
                  bgcolor: '#fcfcfc',
                  transition: 'background-color 0.3s',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <Box sx={{ flexGrow: 1, overflow: 'hidden', mr: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {template.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {template.content}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton size="small" color="info" onClick={() => handleEdit(template)} sx={{ border: '1px solid', borderColor: 'info.main' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(template.id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </CardContent>

      <WhatsappTemplateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        initialData={editingTemplate}
      />

      <ModalConfirmDeactivation 
        open={openModalDeactivation} 
        setOpen={setOpenModalDeactivation} 
        onConfirm={confirmDeactivate} 
      />
    </Card>
  );
};
